import { useState, FormEvent } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useNewsletter } from "@/contexts/NewsletterContext"
import { Mail, CheckCircle2 } from "lucide-react"

type FormState = "idle" | "submitting" | "success" | "error"

export function NewsletterPopup() {
  const { isOpen, close, markSubscribed } = useNewsletter()
  const [email, setEmail] = useState("")
  const [state, setState] = useState<FormState>("idle")
  const [message, setMessage] = useState("")

  const handleClose = () => {
    setState("idle")
    setEmail("")
    setMessage("")
    close()
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setState("submitting")
    setMessage("")

    try {
      const response = await fetch("/uutiskirje/tilaa", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email }),
      })
      const data = await response.json()

      if (response.ok && (data.status === "ok" || data.status === "already_subscribed")) {
        setState("success")
        setMessage(
          data.status === "already_subscribed"
            ? "Tämä sähköposti on jo tilannut uutiskirjeen."
            : "Tarkista sähköpostisi: lähetimme vahvistuslinkin. Tilaus aktivoituu, kun klikkaat linkkiä."
        )
        markSubscribed()
      } else {
        setState("error")
        setMessage(data.message || "Tilaus epäonnistui. Yritä uudelleen hetken kuluttua.")
      }
    } catch {
      setState("error")
      setMessage("Verkkovirhe. Yritä uudelleen hetken kuluttua.")
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="w-11 h-11 rounded-xl bg-primary/10 grid place-items-center mb-2">
            <Mail className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            Tekoälyuutiskirje
          </DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">
            Kerran viikossa suomeksi: käytännöllisiä tekoälyvinkkejä, työkaluja ja
            esimerkkejä. Kirjoittaa Teemu Toivonen, Leanex Oy.
          </DialogDescription>
        </DialogHeader>

        {state === "success" ? (
          <div className="flex items-start gap-3 py-2">
            <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <p className="text-base text-foreground leading-relaxed">{message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 pt-2">
            <input
              type="email"
              required
              autoComplete="email"
              placeholder="etunimi@yritys.fi"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={state === "submitting"}
              className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            {state === "error" && (
              <p className="text-sm text-destructive">{message}</p>
            )}
            <Button type="submit" size="lg" disabled={state === "submitting"} className="w-full">
              {state === "submitting" ? "Lähetetään…" : "Tilaa uutiskirje"}
            </Button>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Käytämme sähköpostiasi vain uutiskirjeen lähetykseen. Voit perua tilauksen
              milloin tahansa. Lue <a href="/tietosuoja" className="underline hover:text-foreground">tietosuojaseloste</a>.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
